import { Check, ImageIcon, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { apiClient } from '@/services/api/client'
import type { LakeDocument } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'

interface ImagePageSelectorProps {
  /** The document to display */
  document: LakeDocument
  /** Whether page 1 is selected */
  isSelected: boolean
  /** Called when the page selection is toggled */
  onToggle: () => void
  /** Optional content rendered below the image (e.g., for consistency with PDF view) */
  children?: React.ReactNode
}

export function ImagePageSelector({
  document,
  isSelected,
  onToggle,
  children,
}: ImagePageSelectorProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null

    const loadImage = async () => {
      try {
        setLoading(true)
        setError(null)

        const token = apiClient.getToken()
        const fetchUrl = lakeApi.getFileUrl(document.s3_key)

        const response = await fetch(fetchUrl, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`)
        }

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        setImageUrl(objectUrl)
        setLoading(false)
      } catch (err) {
        console.error('Failed to load image:', err)
        setError(err instanceof Error ? err.message : 'Failed to load image')
        setLoading(false)
      }
    }

    loadImage()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [document.s3_key])

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        {error}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {isSelected ? '1 page selected' : 'Select page to include in scope'}
        </span>
        <div className="flex items-center gap-2">
          <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Image Document</span>
        </div>
      </div>

      {/* Image view */}
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="flex flex-col items-center gap-4">
          {/* Page header with selection */}
          <div className="w-full max-w-2xl flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <Checkbox checked={isSelected} onCheckedChange={onToggle} className="h-4 w-4" />
              <span className="text-xs font-medium text-foreground">Page 1</span>
            </div>
            {isSelected && (
              <div className="flex items-center gap-1 text-xs text-success">
                <Check className="h-3 w-3" />
                <span>Selected</span>
              </div>
            )}
          </div>

          {/* Image container */}
          <div
            className={`relative w-full max-w-2xl border-2 rounded-lg overflow-hidden transition-colors cursor-pointer ${
              isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onClick={onToggle}
          >
            {loading ? (
              <div className="flex items-center justify-center h-96 bg-muted/30">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : imageUrl ? (
              <img src={imageUrl} alt={document.filename} className="w-full h-auto" />
            ) : null}

            {/* Selection overlay */}
            {isSelected && (
              <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                <Check className="h-4 w-4" />
              </div>
            )}
          </div>

          {/* File info */}
          <div className="text-xs text-muted-foreground text-center">
            <p>{document.filename}</p>
            <p className="mt-0.5">{(document.file_size / 1024).toFixed(1)} KB</p>
          </div>

          {/* Optional children content */}
          {children}
        </div>
      </div>
    </div>
  )
}
